import { OneLineInput } from '../⚛️atoms/OneLineInput';
import React, { useState } from 'react';
import { Accordion, AccordionItem } from '@szhsin/react-accordion';
import { ChevronDownIcon, PlusCircleIcon } from '@heroicons/react/24/solid';
import { WorkbookV2Lesson } from './WorkbookV2Lesson';
import { SortableList } from '../❉molecules/SortableList/SortableList';
import { DragHandle } from '../❉molecules/SortableList/components/SortableItem/SortableItem';
import { TrashIcon } from '@heroicons/react/24/outline';
import { SimpleModal } from '../❉molecules/SimpleModal';
import { getTranslation, updateTranslation } from '../../utils/translationHelper';
import { TranslateButton } from '../atoms/translateButton';

export const WorkbookV2Chapter = ({
  chapter,
  workbook,
  deleteChapter,
  editingLanguage = null,
  onUpdateChapter,
  onCreateLesson,
  onDeleteLesson,
  onLessonsReorder,
  onUpdateLesson,
  onCreateItem,
  onDeleteItem,
  onUploadItemImage,
  onUpdateItem,
  onLessonTranslated
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const headerContent = (
    <div className="flex items-center justify-between gap-2 w-full">
      <div className="flex items-center gap-2">
        <DragHandle />
        <ChevronDownIcon className="size-10 duration-300"></ChevronDownIcon>
        {chapter.title}
      </div>
      <div className="flex items-center gap-2">
        <span className="whitespace-nowrap text-gray-600">
          Chapter {workbook.chapters.findIndex((currentChapter) => currentChapter._id === chapter._id) + 1}
        </span>
        <TrashIcon className="text-pdred/70 size-9" onClick={() => setShowDeleteModal(true)} />
        {showDeleteModal && (
          <SimpleModal hideModal={() => setShowDeleteModal(false)}>
            <div className="flex flex-col items-center gap-2">
              <span>Are you sure you want to delete this chapter?</span>
              <button
                className="bg-pdred rounded-2xl p-6 text-white shadow-md"
                onClick={() => {
                  deleteChapter(chapter._id);
                }}
              >
                Yes
              </button>
            </div>
          </SimpleModal>
        )}
      </div>
    </div>
  );

  return (
    <AccordionItem header={headerContent} className="w-full rounded-lg border-1 border-gray-200 p-2">
      <div className={`grid gap-8 py-2 ${editingLanguage ? 'grid-cols-2' : 'grid-cols-1'}`}>
        <div className="relative">
          <OneLineInput
            value={chapter.title}
            label="Title"
            onChange={(value) => onUpdateChapter(chapter._id, { title: value })}
          ></OneLineInput>
          {editingLanguage && (
            <TranslateButton
              text={chapter.title}
              language={editingLanguage}
              onResponse={(value) =>
                onUpdateChapter(chapter._id, {
                  translations: updateTranslation(chapter.translations ?? {}, editingLanguage, 'title', value)
                })
              }
            />
          )}
        </div>
        {editingLanguage && (
          <OneLineInput
            value={getTranslation(chapter.translations, editingLanguage, 'title')}
            label={`Title (${editingLanguage})`}
            onChange={(value) =>
              onUpdateChapter(chapter._id, {
                translations: updateTranslation(chapter.translations ?? {}, editingLanguage, 'title', value)
              })
            }
          ></OneLineInput>
        )}
        <OneLineInput
          className="col-start-1"
          value={chapter.description}
          label="Description"
          onChange={(value) => onUpdateChapter(chapter._id, { description: value })}
        ></OneLineInput>
        {editingLanguage && (
          <OneLineInput
            value={getTranslation(chapter.translations, editingLanguage, 'description')}
            label={`Description (${editingLanguage})`}
            onChange={(value) =>
              onUpdateChapter(chapter._id, {
                translations: updateTranslation(chapter.translations ?? {}, editingLanguage, 'description', value)
              })
            }
          ></OneLineInput>
        )}
        <span className="col-span-2 w-full border-b border-gray-200 text-2xl">Lessons</span>
        <Accordion allowZeroExpanded className={`${editingLanguage ? 'col-span-2' : ''}`}>
          <SortableList
            items={chapter.lessons ?? []}
            onChange={(lessons) => onLessonsReorder(chapter._id, lessons)}
            renderItem={(lesson) => (
              <SortableList.Item id={lesson._id} data-id={lesson._id}>
                <div className="w-full">
                  <WorkbookV2Lesson
                    workbook={workbook}
                    chapter_id={chapter._id}
                    lesson={lesson}
                    key={lesson._id}
                    deleteLesson={onDeleteLesson}
                    editingLanguage={editingLanguage}
                    onUpdateLesson={onUpdateLesson}
                    onCreateItem={onCreateItem}
                    onDeleteItem={onDeleteItem}
                    onUploadItemImage={onUploadItemImage}
                    onUpdateItem={onUpdateItem}
                    onLessonTranslated={onLessonTranslated}
                  ></WorkbookV2Lesson>
                </div>
              </SortableList.Item>
            )}
          />
        </Accordion>
        <div
          className="group col-span-2 flex items-center self-center hover:cursor-pointer"
          onClick={() => onCreateLesson(chapter._id)}
        >
          <PlusCircleIcon className="text-pdteal size-12 self-center drop-shadow-md hover:cursor-pointer" />
          <div className="block w-0 overflow-hidden whitespace-nowrap transition-[width] duration-500 group-hover:w-50">
            Add a Lesson
          </div>
        </div>
      </div>
    </AccordionItem>
  );
};
